const DISMISSED_AT_KEY = "sell-it-off:notification-setup-banner:dismissed-at";
const DISMISS_COUNT_KEY = "sell-it-off:notification-setup-banner:dismiss-count";
const SESSION_BANNER_SHOWN_KEY =
  "sell-it-off:notification-setup-banner:session-shown";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const NOTIFICATION_SETUP_SNOOZE_DEFAULT = {
  firstDismissDays: 7,
  secondDismissDays: 30,
  passiveOnlyDismissCount: 3,
} as const;

/** Gentler snooze when only push is missing and near-me is already complete. */
export const NOTIFICATION_SETUP_SNOOZE_PUSH_ONLY_SOFT = {
  firstDismissDays: 14,
  secondDismissDays: 30,
  passiveOnlyDismissCount: 2,
} as const;

export type NotificationSetupSnoozeProfile = "default" | "push_only_soft";

export type NotificationSetupBannerPolicyInput = {
  hasSetupGap: boolean;
  dismissedAtIso: string | null;
  dismissCount: number;
  snoozeProfile?: NotificationSetupSnoozeProfile;
  now?: Date;
};

export type NotificationSetupBannerPolicyResult = {
  show: boolean;
  passiveOnly: boolean;
  snoozedUntilIso: string | null;
};

function parseIso(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function getNotificationSetupSnoozeConfig(
  profile: NotificationSetupSnoozeProfile,
): {
  firstDismissDays: number;
  secondDismissDays: number;
  passiveOnlyDismissCount: number;
} {
  return profile === "push_only_soft"
    ? NOTIFICATION_SETUP_SNOOZE_PUSH_ONLY_SOFT
    : NOTIFICATION_SETUP_SNOOZE_DEFAULT;
}

function snoozeDaysForDismissCount(
  dismissCount: number,
  profile: NotificationSetupSnoozeProfile,
): number | null {
  const config = getNotificationSetupSnoozeConfig(profile);
  if (dismissCount <= 0) return null;
  if (dismissCount === 1) return config.firstDismissDays;
  return config.secondDismissDays;
}

export function computeNotificationSetupBannerSnoozeUntil(
  dismissedAtIso: string | null,
  dismissCount: number,
  profile: NotificationSetupSnoozeProfile = "default",
): Date | null {
  const dismissedAt = parseIso(dismissedAtIso);
  if (!dismissedAt || dismissCount <= 0) return null;
  const days = snoozeDaysForDismissCount(dismissCount, profile);
  if (days == null) return null;
  return new Date(dismissedAt.getTime() + days * MS_PER_DAY);
}

export function resolveNotificationSetupSnoozeProfile(input: {
  nearMeGap: boolean;
  pushGap: boolean;
}): NotificationSetupSnoozeProfile {
  if (input.pushGap && !input.nearMeGap) {
    return "push_only_soft";
  }
  return "default";
}

export function shouldShowNotificationSetupBanner(
  input: NotificationSetupBannerPolicyInput,
): NotificationSetupBannerPolicyResult {
  if (!input.hasSetupGap) {
    return { show: false, passiveOnly: false, snoozedUntilIso: null };
  }

  const profile = input.snoozeProfile ?? "default";
  const config = getNotificationSetupSnoozeConfig(profile);

  if (input.dismissCount >= config.passiveOnlyDismissCount) {
    return { show: false, passiveOnly: true, snoozedUntilIso: null };
  }

  const now = input.now ?? new Date();
  const snoozedUntil = computeNotificationSetupBannerSnoozeUntil(
    input.dismissedAtIso,
    input.dismissCount,
    profile,
  );
  if (snoozedUntil && now < snoozedUntil) {
    return {
      show: false,
      passiveOnly: false,
      snoozedUntilIso: snoozedUntil.toISOString(),
    };
  }

  return { show: true, passiveOnly: false, snoozedUntilIso: null };
}

export function readNotificationSetupBannerDismissCount(): number {
  if (typeof window === "undefined") return 0;
  const raw = localStorage.getItem(DISMISS_COUNT_KEY);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function readNotificationSetupBannerDismissedAt(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(DISMISSED_AT_KEY);
}

export function recordNotificationSetupBannerDismissed(nowIso?: string): {
  dismissCount: number;
  dismissedAtIso: string;
} {
  const dismissedAtIso = nowIso ?? new Date().toISOString();
  if (typeof window === "undefined") {
    return { dismissCount: 1, dismissedAtIso };
  }
  const nextCount = readNotificationSetupBannerDismissCount() + 1;
  localStorage.setItem(DISMISS_COUNT_KEY, String(nextCount));
  localStorage.setItem(DISMISSED_AT_KEY, dismissedAtIso);
  return { dismissCount: nextCount, dismissedAtIso };
}

export function clearNotificationSetupBannerDismissState(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(DISMISS_COUNT_KEY);
  localStorage.removeItem(DISMISSED_AT_KEY);
}

export function readNotificationSetupBannerSessionShown(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(SESSION_BANNER_SHOWN_KEY) === "1";
}

export function markNotificationSetupBannerSessionShown(): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(SESSION_BANNER_SHOWN_KEY, "1");
}

export function clearNotificationSetupBannerSessionShown(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(SESSION_BANNER_SHOWN_KEY);
}

export function resetNotificationSetupBannerClientState(): void {
  clearNotificationSetupBannerDismissState();
  clearNotificationSetupBannerSessionShown();
}
